


import React, { useState, useEffect } from 'react';
import { Switch } from '@mui/material';
import Plot from 'react-plotly.js';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import ifav from './ifav.png';
import useSohChartQuery from '../../utils/useSohChartQuery';
import Shimmer_graph from '../shimmer_graph';
import { ManText } from '@craft-design-system/core-components-react';

const SOH = ({ value }) => {
  const { vin_short } = value || {};
  const [showECU, setShowECU] = useState("Model");
  const [sohChartData, setSohChartData] = useState({ data: [], layout: {} });
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { fetchSohChart } = useSohChartQuery();

  const popover = (
    <Popover id="popover-basic" >
      <Popover.Body>
        <p><strong>Definition</strong></p>

        <p>
          The state of health (<strong>SoH</strong>) is a measure of battery capacity
          relative to when it was new. The <strong>SoH</strong> value is a linear
          combination of calendaric aging loss and cyclic aging loss.
        </p>

        <p>
          Two sources are presented:
        </p>

        <ul>
          <li>
            <strong>SoH from MAN Battery Aging Model</strong>
          </li>
          <li>
            <strong>SoH from ECU</strong>
          </li>
        </ul>

        <p>
          Telematic raw data for <strong>“SoH from ECU”</strong>:
        </p>

        <ul>
          <li>
            <strong>TBM3: </strong>
            payloaddecoded_vehiclestate_batteryinfo_stateofhealth
          </li>
          <li>
            <strong>CM4: </strong>
            payloaddecoded_vehiclestate_batteryinfo_systemstateofhealth
          </li>
        </ul>

        <p><strong>Source</strong></p>

        <p>
          Based on MAN Battery Aging Model calculations and ECU-reported battery
          state-of-health values.
        </p>

        <p><strong>Visualization</strong></p>

        <ul>
          <li>
            <strong>Single vehicle: </strong>
            <strong> SoH</strong> is visualized over time since the start of data
            acquisition, with both sources displayed side by side
            (model vs. ECU).
          </li>

          <li>
            <strong>Multiple vehicles: </strong>
            <strong> SoH</strong> over time is shown as three percentile curves
            (10th percentile, median, and 90th percentile).
            The y-axis represents <strong>SoH</strong> (%), and the x-axis represents
            time (days since the start of data collection).
          </li>

          <li>
            This percentile view highlights the typical range of
            <strong> SoH</strong> values across vehicles. At longer time spans
            (far right of the chart), the <strong>SoH</strong> curves may rise
            slightly because only a few vehicles have data at those extended
            durations. This is expected behavior and not a graph error.
          </li>

          <li>
            Users can select which source
            (<strong>model</strong> or <strong>ECU</strong>) to visualize.
          </li>
        </ul>
      </Popover.Body>
    </Popover>
  );

  // Set showECU based on VIN count
  useEffect(() => {
    const fetchData = async () => {
      if (!vin_short || vin_short.length === 0 || !showECU) return;

      setIsLoading(true);
      try {
        const data = await fetchSohChart({
          vinShort: vin_short,
          showECU
        });

        setSohChartData(data || { data: [], layout: {} });
      } catch (error) {
        console.error("Error fetching SOH chart data", error);
        setSohChartData({ data: [], layout: {} });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData(); // ✅ Always fetch when dependencies are ready
  }, [showECU, JSON.stringify(vin_short)]);


  const vinCount = vin_short?.length || 0;

  return (
    <div style={{ padding: '16px' }}>
      {/* Title and Info Icon */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <ManText className="card-title-text" variant="component-l" as="span" size="l">SoH</ManText>
        </div>
        <OverlayTrigger trigger="hover" placement="right" overlay={popover}>
          <img src={ifav} alt="info" style={{ width: 20, height: 20, cursor: 'pointer' }} />
        </OverlayTrigger>
      </div>

      {/* Switch */}
      {vinCount > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <ManText variant="body-sm">Model</ManText>
          <Switch
            checked={showECU === "ECU"}
            onChange={() => setShowECU(prev => (prev === "Model" ? "ECU" : "Model"))}
            color="primary"
          />
          <ManText variant="body-sm">ECU</ManText>
        </div>
      )}

      {/* Chart or Shimmer */}
      <div>
        {isLoading ? (
          <Shimmer_graph />
        ) : sohChartData?.data?.length > 0 ? (
          <Plot
            data={sohChartData.data}
            layout={{
              ...sohChartData.layout,
              title: '',
              margin: { t: 10, r: 10, l: 10, b: 170 }
            }}
            useResizeHandler
            style={{ width: '100%', height: '450px' }}
            config={{ responsive: true }}
          />
        ) : (
          <ManText>No Data Available</ManText>
        )}
      </div>
    </div>
  );
};

export default SOH;